/**
 * Loader idempotente do script oficial Memed.
 */

import {
  getMemedScriptUrl,
  MEMED_MODULE_NAME,
  MEMED_SCRIPT_ELEMENT_ID,
} from './memed.config';
import { MEMED_ERROR_CODES, MemedFrontendError } from './memed.errors';
import type { MemedModuleInfo } from './memed.types';

let loadPromise: Promise<void> | null = null;
let lastLoadFailed = false;

function isModuleReady(): boolean {
  return Boolean(window.MdSinapsePrescricao && window.MdHub);
}

function subscribeModuleInit(onInit: (payload?: unknown) => void): void {
  const bus = window.MdSinapsePrescricao?.event;
  if (typeof bus?.listen === 'function') {
    bus.listen('core:moduleInit', onInit);
    return;
  }
  if (typeof bus?.add === 'function') {
    bus.add('core:moduleInit', onInit);
  }
}

function waitForPrescriptionModule(timeoutMs = 20000): Promise<void> {
  if (isModuleReady()) {
    // MdHub pode existir antes do moduleInit; ainda assim resolve se bridge presente.
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        // Se hub já existe, aceita mesmo sem novo moduleInit (reload idempotente).
        if (window.MdHub) {
          resolve();
        } else {
          reject(
            new MemedFrontendError({
              code: MEMED_ERROR_CODES.SCRIPT_LOAD_FAILED,
              message: 'Tempo esgotado ao carregar o módulo Memed.',
              kind: 'technical',
              allowLegacyFallback: true,
            }),
          );
        }
      }, timeoutMs);

      const onInit = (payload?: unknown) => {
        const module = payload as MemedModuleInfo | undefined;
        if (module?.name !== MEMED_MODULE_NAME) return;
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve();
      };

      try {
        subscribeModuleInit(onInit);
      } catch {
        // Se add falhar mas MdHub já existe, segue.
        if (window.MdHub) {
          settled = true;
          window.clearTimeout(timer);
          resolve();
        }
      }

      // Já pronto?
      if (window.MdHub && window.MdSinapsePrescricao) {
        // Mantém listener; se moduleInit já passou, timeout resolve via MdHub.
        window.setTimeout(() => {
          if (!settled && window.MdHub) {
            settled = true;
            window.clearTimeout(timer);
            resolve();
          }
        }, 50);
      }
    });
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(
        new MemedFrontendError({
          code: MEMED_ERROR_CODES.SCRIPT_LOAD_FAILED,
          message: 'Tempo esgotado ao carregar o script Memed.',
          kind: 'technical',
          allowLegacyFallback: true,
        }),
      );
    }, timeoutMs);

    const onInit = (payload?: unknown) => {
      const module = payload as MemedModuleInfo | undefined;
      if (module?.name !== MEMED_MODULE_NAME) return;
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };

    const poll = window.setInterval(() => {
      if (!window.MdSinapsePrescricao) return;
      window.clearInterval(poll);
      try {
        subscribeModuleInit(onInit);
      } catch (error) {
        if (!settled) {
          settled = true;
          window.clearTimeout(timer);
          reject(error);
        }
      }
    }, 50);
  });
}

/**
 * Carrega o script Memed uma vez (idempotente).
 * Token vai apenas em data-token (requisito Memed), nunca em storage.
 */
export function loadMemedScript(token: string): Promise<void> {
  if (!token || !token.trim()) {
    return Promise.reject(
      new MemedFrontendError({
        code: MEMED_ERROR_CODES.SESSION_INVALID,
        message: 'Token de sessão Memed inválido.',
        kind: 'technical',
        allowLegacyFallback: true,
      }),
    );
  }

  if (isModuleReady() && !lastLoadFailed) {
    return Promise.resolve();
  }

  if (loadPromise && !lastLoadFailed) {
    return loadPromise;
  }

  lastLoadFailed = false;
  loadPromise = (async () => {
    const existing = document.getElementById(MEMED_SCRIPT_ELEMENT_ID) as HTMLScriptElement | null;
    if (existing && isModuleReady()) {
      return;
    }

    if (existing && !isModuleReady()) {
      existing.remove();
    }

    const scriptUrl = getMemedScriptUrl();
    const script = document.createElement('script');
    script.id = MEMED_SCRIPT_ELEMENT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = scriptUrl;
    script.setAttribute('data-token', token.trim());

    const loadResult = new Promise<void>((resolve, reject) => {
      script.onload = () => resolve();
      script.onerror = () => {
        script.remove();
        reject(
          new MemedFrontendError({
            code: MEMED_ERROR_CODES.SCRIPT_LOAD_FAILED,
            message: 'Não foi possível carregar o script da prescrição digital.',
            kind: 'technical',
            allowLegacyFallback: true,
          }),
        );
      };
    });

    document.body.appendChild(script);
    await loadResult;
    await waitForPrescriptionModule();
  })()
    .then(() => {
      lastLoadFailed = false;
    })
    .catch((error) => {
      lastLoadFailed = true;
      loadPromise = null;
      throw error;
    });

  return loadPromise;
}

/** Permite nova tentativa após falha (testes / recovery). */
export function resetMemedScriptLoaderForTests(): void {
  loadPromise = null;
  lastLoadFailed = false;
  const existing = document.getElementById(MEMED_SCRIPT_ELEMENT_ID);
  existing?.remove();
}
